import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './RefundRequestPage.css';

const REFUND_REASONS = [
	{ value: 'change_mind', label: '단순변심', responsibility: 'customer' },
	{ value: 'wrong_order', label: '잘못 주문', responsibility: 'customer' },
	{ value: 'defect', label: '상품불량/파손', responsibility: 'seller' },
	{ value: 'wrong_delivery', label: '오배송', responsibility: 'seller' },
	{ value: 'missing_item', label: '누락', responsibility: 'seller' },
	{ value: 'different_product', label: '상품상이', responsibility: 'seller' },
	{ value: 'other', label: '기타', responsibility: 'customer' },
];

export default function RefundRequestPage() {
	const { orderId, itemId } = useParams();
	const navigate = useNavigate();
	
	const [orderItem, setOrderItem] = useState(null);
	const [order, setOrder] = useState(null);
	const [reason, setReason] = useState('');
	const [reasonDetail, setReasonDetail] = useState('');
	const [images, setImages] = useState([null, null]);
	const [previews, setPreviews] = useState([null, null]);
	const [loading, setLoading] = useState(false);
	const [pageLoading, setPageLoading] = useState(true);
	const [error, setError] = useState('');

	useEffect(() => {
		if (orderId && itemId) {
			fetchOrderItem();
		}
	}, [orderId, itemId]);

	const fetchOrderItem = async () => {
		try {
			const token = localStorage.getItem('access_token');
			const res = await axios.get(
				`https://api.weartoday.kr/api/orders/${orderId}/`,
				{ headers: { 'Authorization': `Bearer ${token}` } }
			);
			
			const item = res.data.items.find(i => i.id === parseInt(itemId));
			if (item) {
				setOrderItem(item);
				setOrder(res.data);
			} else {
				setError('상품을 찾을 수 없습니다.');
			}
		} catch (err) {
			console.error('주문 조회 실패:', err);
			setError('주문 정보를 불러올 수 없습니다.');
			if (err.response?.status === 401) {
				navigate('/login');
			}
		} finally {
			setPageLoading(false);
		}
	};

	const getImageUrl = (url) => {
		if (!url) return 'https://via.placeholder.com/100';
		if (url.startsWith('/media')) {
			return `https://api.weartoday.kr${url}`;
		}
		return url;
	};

	const handleImageChange = (index, e) => {
		const file = e.target.files[0];
		if (file) {
			if (file.size > 5 * 1024 * 1024) {
				alert('이미지는 5MB 이하만 업로드 가능합니다.');
				return;
			}
			
			const newImages = [...images];
			newImages[index] = file;
			setImages(newImages);
			
			const reader = new FileReader();
			reader.onloadend = () => {
				const newPreviews = [...previews];
				newPreviews[index] = reader.result;
				setPreviews(newPreviews);
			};
			reader.readAsDataURL(file);
		}
	};

	const removeImage = (index) => {
		const newImages = [...images];
		const newPreviews = [...previews];
		newImages[index] = null;
		newPreviews[index] = null;
		setImages(newImages);
		setPreviews(newPreviews);
	};

	const handleSubmit = async () => {
		if (!reason) {
			alert('환불 사유를 선택해주세요.');
			return;
		}

		setLoading(true);
		setError('');

		try {
			const token = localStorage.getItem('access_token');
			const formData = new FormData();
			formData.append('order', orderId);
			formData.append('order_item', itemId);
			formData.append('reason', reason);
			formData.append('reason_detail', reasonDetail);
			
			if (images[0]) formData.append('image1', images[0]);
			if (images[1]) formData.append('image2', images[1]);

			await axios.post(
				'https://api.weartoday.kr/api/orders/refunds/',
				formData,
				{
					headers: {
						'Authorization': `Bearer ${token}`,
						'Content-Type': 'multipart/form-data'
					}
				}
			);

			alert('환불 신청이 완료되었습니다.');
			navigate('/orders');
		} catch (err) {
			console.error('환불 신청 실패:', err);
			const errMsg = err.response?.data?.non_field_errors?.[0] 
				|| err.response?.data?.detail 
				|| err.response?.data?.error 
				|| '환불 신청에 실패했습니다.';
			setError(errMsg);
		} finally {
			setLoading(false);
		}
	};

	const selectedReason = REFUND_REASONS.find(r => r.value === reason);

	if (pageLoading) {
		return (
			<div className="refund-container">
				<div className="loading-container">⏳ 로딩 중...</div>
			</div>
		);
	}

	if (error && !orderItem) {
		return (
			<div className="refund-container">
				<div className="error-container">
					<p>{error}</p>
					<button onClick={() => navigate('/orders')}>주문목록으로</button>
				</div>
			</div>
		);
	}

	return (
		<div className="refund-container">
			<h1 className="refund-title">환불 신청</h1>

			{/* 상품 정보 */}
			<div className="product-card">
				<img 
					src={getImageUrl(orderItem?.product_image)} 
					alt={orderItem?.product_name}
					className="product-image"
				/>
				<div className="product-info">
					<p className="product-name">{orderItem?.product_name}</p>
					<p className="product-option">
						{orderItem?.option_name && `${orderItem.option_name} / `}
						{orderItem?.size}
					</p>
					<p className="product-price">
						{orderItem?.quantity}개 · ₩{orderItem?.total_price?.toLocaleString()}
					</p>
				</div>
			</div>

			{/* 환불 사유 선택 */}
			<div className="reason-section">
				<p className="section-title">환불 사유 선택 *</p>
				<div className="reason-list">
					{REFUND_REASONS.map((item) => (
						<label 
							key={item.value}
							className={`reason-item ${reason === item.value ? 'selected' : ''}`}
						>
							<input
								type="radio"
								name="reason"
								value={item.value}
								checked={reason === item.value}
								onChange={(e) => setReason(e.target.value)}
								className="reason-radio"
							/>
							<span className="reason-label">{item.label}</span>
							<span className={`reason-badge ${item.responsibility}`}>
								{item.responsibility === 'seller' ? '판매자 책임' : '고객 책임'}
							</span>
						</label>
					))}
				</div>
			</div>

			{/* 상세 사유 */}
			<div className="detail-section">
				<p className="section-title">
					상세 사유 <span className="section-subtitle">(선택, 50자 이내)</span>
				</p>
				<input
					type="text"
					value={reasonDetail}
					onChange={(e) => setReasonDetail(e.target.value.slice(0, 50))}
					placeholder="상세 사유를 입력해주세요"
					className="detail-input"
					maxLength={50}
				/>
				<p className="char-count">{reasonDetail.length}/50</p>
			</div>

			{/* 사진 업로드 */}
			<div className="image-section">
				<p className="section-title">
					사진 첨부 <span className="section-subtitle">(선택, 최대 2장)</span>
				</p>
				<div className="image-upload-area">
					{[0, 1].map((index) => (
						<div key={index} className="image-box">
							{previews[index] ? (
								<>
									<img 
										src={previews[index]} 
										alt={`첨부사진 ${index + 1}`}
										className="image-preview"
									/>
									<button
										type="button"
										onClick={() => removeImage(index)}
										className="image-remove-btn"
									>
										×
									</button>
								</>
							) : (
								<label className="image-upload-btn">
									<span className="image-upload-icon">+</span>
									<span className="image-upload-text">사진 {index + 1}</span>
									<input
										type="file"
										accept="image/*"
										onChange={(e) => handleImageChange(index, e)}
									/>
								</label>
							)}
						</div>
					))}
				</div>
				<p className="image-help">
					* 상품 불량/파손의 경우 사진을 첨부하면 빠른 처리가 가능합니다.
				</p>
			</div>

			{/* 환불 안내 */}
			{selectedReason && (
				<div className={`refund-info-box ${selectedReason.responsibility}`}>
					<p className="refund-info-title">
						{selectedReason.responsibility === 'seller' 
							? '✅ 판매자 책임 (반품배송비 무료)' 
							: '⚠️ 고객 책임 (반품배송비 3,000원 차감)'}
					</p>
					<p className="refund-info-desc">
						{selectedReason.responsibility === 'seller'
							? '상품금액 전액 + 배송비가 환불됩니다.'
							: '상품금액에서 반품배송비 3,000원이 차감된 금액이 환불됩니다.'}
					</p>
				</div>
			)}

			{error && <div className="error-box">{error}</div>}

			{/* 버튼 영역 */}
			<div className="refund-buttons">
				<button
					type="button"
					onClick={() => navigate(-1)}
					className="btn-back-refund"
				>
					취소
				</button>
				<button
					type="button"
					onClick={handleSubmit}
					disabled={loading || !reason}
					className="submit-btn"
				>
					{loading ? '신청 중...' : '환불 신청하기'}
				</button>
			</div>
		</div>
	);
}